// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { DSNSnapshot } from '../types';
import { COMPLEX_META } from '../data/complexes';
import { extractPairs } from '../state';
import { getSpacecraftInfo, CATEGORY_COLOR } from '../data/spacecraft';

let map: L.Map | null = null;
let markerLayer: L.LayerGroup | null = null;

function ensureMap(): L.Map {
  if (map) return map;

  const container = document.getElementById('dsn-map');
  if (!container) throw new Error('map container missing');

  map = L.map(container, {
    center: [15, 0],
    zoom: 2,
    minZoom: 1,
    maxZoom: 6,
    worldCopyJump: true,
    zoomControl: true,
    attributionControl: true,
    preferCanvas: false,
  });

  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
    maxZoom: 18,
    subdomains: 'abcd',
    attribution: '© OpenStreetMap contributors · © CARTO',
  }).addTo(map);

  markerLayer = L.layerGroup().addTo(map);
  return map;
}

export function renderMap(snapshot: DSNSnapshot | null): void {
  const m = ensureMap();
  if (!markerLayer) return;
  markerLayer.clearLayers();

  const pairs = snapshot ? extractPairs(snapshot) : [];

  for (const complex of Object.entries(COMPLEX_META)) {
    const [code, meta] = complex;
    const complexPairs = pairs.filter((p) => p.complex === code);
    const activeSpacecraft = [...new Set(complexPairs.filter((p) => p.spacecraft && p.spacecraft !== 'DSN' && p.spacecraft !== 'DSS').map((p) => p.spacecraft))];
    const isActive = activeSpacecraft.length > 0;
    const color = isActive ? '#f5a623' : '#6e7ba6';

    const pulseIcon = L.divIcon({
      className: 'dsn-marker',
      html: `
        <div style="position: relative; width: 32px; height: 32px;">
          ${isActive ? `<div style="position:absolute; inset:0; border-radius:50%; background:${color}; opacity:0.25; animation: marker-pulse 2s infinite;"></div>` : ''}
          <div style="position:absolute; top:8px; left:8px; width:16px; height:16px; border-radius:50%; background:${color}; border: 2px solid #0a0e1a; box-shadow: 0 0 8px ${color};"></div>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });

    const friendlyName = code === 'gdscc' ? 'Goldstone' : code === 'mdscc' ? 'Madrid' : 'Canberra';
    const marker = L.marker([meta.lat, meta.lon], { icon: pulseIcon }).addTo(markerLayer);
    marker.bindTooltip(
      `${friendlyName} · ${meta.country} — ${isActive ? `tracking ${activeSpacecraft.length} spacecraft` : 'no active tracks'} · click for details`,
      { direction: 'top', offset: [0, -12], className: 'dsn-tip' },
    );

    const scList = activeSpacecraft
      .map((s) => {
        const info = getSpacecraftInfo(s);
        const col = CATEGORY_COLOR[info.category];
        return `<div style="display:flex; align-items:center; gap:6px; font-size:12px; margin-top:3px;"><span style="width:8px;height:8px;border-radius:50%;background:${col};"></span>${info.name}</div>`;
      })
      .join('');

    marker.bindPopup(`
      <div style="font-family: var(--font-sans); color: #eef2ff; min-width: 220px;">
        <div style="font-size: 15px; font-weight: 600; margin-bottom: 4px;">${friendlyName}</div>
        <div style="font-size: 11px; color: #b0b9d9; margin-bottom: 8px;">${meta.country} · Est. ${meta.established} · Elev. ${meta.elevation} m</div>
        <div style="font-size: 11px; color: #6e7ba6; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 4px;">Tracking now (${activeSpacecraft.length})</div>
        ${scList || '<div style="color:#6e7ba6; font-size:12px;">No active tracks</div>'}
      </div>
    `);
  }

  m.invalidateSize();
}

export function mountMapOverlay(): string {
  return `
    <div class="view-header">
      <h2 class="view-title">Global view</h2>
      <p class="view-subtitle">
        The three <span class="glossary-link" data-term="complex">DSN complexes</span> are spaced ~120° apart around the Earth so at least one can always see any distant spacecraft as the planet rotates.
        Amber pulses mean the complex is currently tracking at least one mission. Click a marker for details.
      </p>
    </div>
    <div class="map-wrap">
      <div id="dsn-map" role="region" aria-label="World map of DSN complexes"></div>
      <div class="map-overlay">
        <h4>Legend</h4>
        <div class="map-legend">
          <div class="map-legend-row"><span style="width:10px;height:10px;border-radius:50%;background:#f5a623;display:inline-block;"></span> Actively tracking</div>
          <div class="map-legend-row"><span style="width:10px;height:10px;border-radius:50%;background:#6e7ba6;display:inline-block;"></span> Idle / maintenance</div>
        </div>
      </div>
    </div>
  `;
}

export function destroyMap(): void {
  if (map) {
    map.remove();
    map = null;
    markerLayer = null;
  }
}
